import React, {useState, ChangeEvent, useEffect} from 'react'
import { useHistory } from "react-router";

import { usePersistedState } from "hooks"
import { entities } from "reduxStore"

import { Location } from 'components/BillingLocationSelector'
import {
	convertAvailabilityForBackend,
	convertAvailabilityForFrontend,
	FirstStep,
	EditedJob,
	Requirements,
	Days,
	Positions,
	Basis,
	SelectOption,
	ExitPopup,
	BackendJob,
	EditPostMode,
	PositionId,
	BasisId,
	RequirementId,
	isValidPositionId,
	isValidBasisId,
	isValidRequirementId,
	isValidDayId,
	findPositionById,
	findBasisById,
	findRequirementById,
	findDayById,
} from "components/EditJob";


export default function EditJobPostFirstStep() {
	const history = useHistory()
	const { mode, originalJob } = history.location.state as { mode: EditPostMode, originalJob?: BackendJob }

	const [isExitPopupOpen, setIsExitPopupOpen] = useState(false);

	const billingLocations = usePersistedState({
		entityName: entities.dentistBillingLocations,
		initialValue: [],
	})[0] as Location[]

	const [persistedJob, saveJob] = usePersistedState({
		entityName: entities.editedJobPost,
		initialValue: {} as EditedJob,
	})

	// Listen for feature-on-post preference from FirstStep component and persist it
	useEffect(() => {
		const handler = (e: any) => {
			const checked = e?.detail?.value === true;
			saveJob({ ...persistedJob, featureOnPost: checked });
		};
		window.addEventListener('featureOnPostPreference', handler as any);
		return () => window.removeEventListener('featureOnPostPreference', handler as any);
	}, [persistedJob, saveJob]);

	const [positionValue, setPositionValue] = useState<PositionId | undefined>(
		persistedJob.position ? persistedJob.position.id as PositionId : undefined
	)
	const [positionValueError, setPositionValueError] = useState('');

	const [basisValue, setBasisValue] = useState<BasisId | undefined>(
		persistedJob.basis ? persistedJob.basis.id as BasisId : undefined
	)
	const [basisValueError, setBasisValueError] = useState('');

	const [locationValue, setLocationValue] = useState<number | undefined>(
		persistedJob.location ? persistedJob.location.id : undefined
	)
	const [locationValueError, setLocationValueError] = useState('');

	const [requirementValue, setRequirementValue] = useState<RequirementId | undefined>(
		persistedJob.requirement ? persistedJob.requirement.id as RequirementId : undefined
	)
	const [requirementValueError, setRequirementValueError] = useState('');

	const [availabilityArray, setAvailabilityArray] = useState<SelectOption[]>(
		persistedJob.availability ? convertAvailabilityForFrontend(persistedJob.availability) : []
	)
	const [availabilityArrayError, setAvailabilityArrayError] = useState('');

	const [startDate, setStartDate] = useState<Date | null>(
		persistedJob.startDate ? persistedJob.startDate : null
	);
	const [startDateError, setStartDateError] = useState('');

	const [approximateStartDate, setApproximateStartDate] = useState(persistedJob.approxStart || false);

	const [isOrthodonticFlag, setIsOrthodonticFlag] = useState(persistedJob.isOrtho || false);
	const [isRestorativeFlag, setIsRestorativeFlag] = useState(persistedJob.isRestorative || false);

	const [isPublic, setIsPublic] = useState<boolean>(persistedJob.isPublic || true)

	const [notificationEmail, setNotificationEmail] = useState(persistedJob.notificationEmail || '')
	const [editedEmail, setEditedEmail] = useState('')
    const [editedEmailError, setEditedEmailError] = useState('')
	const EMAIL_VALIDATION_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;

	useEffect(() => {
		// When in 'repost' mode, pre-fill the form with data from the original job
		if (mode === 'repost' && originalJob) {
			// Create a new object from originalJob to avoid mutating it
			const repostJobData: EditedJob = {
				...persistedJob, // Keep any existing persisted data
				position: findPositionById(originalJob.jobType as PositionId) || undefined,
				basis: originalJob.basis !== undefined ? findBasisById(originalJob.basis as BasisId) || undefined : undefined,
				location: billingLocations.find(loc => loc.id === originalJob.locationId),
				requirement: findRequirementById(originalJob.minYearsExp as RequirementId) || undefined,
				availability: originalJob.daysOfWeek ? convertAvailabilityForBackend(convertAvailabilityForFrontend(originalJob.daysOfWeek.split(',').map(Number) as any)) : undefined,
				approxStart: originalJob.approxStart,
				isOrtho: originalJob.isOrtho,
				isRestorative: originalJob.isRestorative,
				isPublic: originalJob.isPublic === undefined ? true : originalJob.isPublic,
				notificationEmail: originalJob.sendEmailTo,
				title: originalJob.title,
				description: originalJob.description,
				// Per ticket requirements, clear time-sensitive fields
				startDate: undefined,
				// Ensure no ID is carried over, so a new job is created
				id: undefined,
			};
	
			// Save the pre-populated data to persisted state
			saveJob(repostJobData);
	
			// Also update the local state of the component
			if (originalJob.jobType !== undefined) setPositionValue(originalJob.jobType as PositionId);
			if (originalJob.basis !== undefined && isValidBasisId(originalJob.basis)) {
				setBasisValue(originalJob.basis);
			}
			if (originalJob.locationId) setLocationValue(originalJob.locationId);
			if (originalJob.minYearsExp !== undefined) setRequirementValue(originalJob.minYearsExp as RequirementId);
			if (originalJob.daysOfWeek) {
				setAvailabilityArray(convertAvailabilityForFrontend(originalJob.daysOfWeek.split(',').map(Number) as any));
			}
			setStartDate(null); // Explicitly clear start date
			setApproximateStartDate(originalJob.approxStart || false);
			setIsOrthodonticFlag(originalJob.isOrtho || false);
			setIsRestorativeFlag(originalJob.isRestorative || false);
			setIsPublic(originalJob.isPublic === undefined ? true : originalJob.isPublic);
			setNotificationEmail(originalJob.sendEmailTo || '');
			setEditedEmail(originalJob.sendEmailTo || '');
		}
	}, [mode, originalJob, saveJob]); // Dependency array ensures this runs only once on load

	const onSetStartDate = (date: Date) => {
		startDateError && setStartDateError('');
		setStartDate(date);
	}

	const onSetAvailabilityOption = (id: number, checked: boolean) => {
		availabilityArrayError && setAvailabilityArrayError('')

		if (checked && !isValidDayId(id)) {
			console.warn(`Invalid day ID: ${id}`);
			return;
		}

		setAvailabilityArray((prevState => {
			if (!checked) {
				return prevState.filter(option => option.id !== id)
			} else {
				const day = findDayById(id);
				return day ? [...prevState, day] : prevState;
			}
		}))
	}

	const onSetPositionValue = (value: number) => {
		if (!isValidPositionId(value)) {
			console.warn(`Invalid position ID: ${value}`);
			return;
		}

		positionValueError && setPositionValueError('');
		setPositionValue(value);
		if (value > 0) {
			setIsOrthodonticFlag(false);
			setIsRestorativeFlag(false);
		}
	}

	const onSetBasisValue = (value: number) => {
		if (!isValidBasisId(value)) {
			console.warn(`Invalid basis ID: ${value}`);
			return;
		}

		basisValueError && setBasisValueError('');
		setBasisValue(value);
	}

	const onSetLocationValue = (value: number) => {
		if (!billingLocations.some(location => location.id === value)) {
			console.warn(`Invalid location ID: ${value}`);
			return;
		}

		locationValueError && setLocationValueError('');
		setLocationValue(value);
	}

	const onSetRequirementValue = (value: number) => {
		if (!isValidRequirementId(value)) {
			console.warn(`Invalid requirement ID: ${value}`);
			return;
		}

		requirementValueError && setRequirementValueError('');
		setRequirementValue(value);
	}

	const onEditedEmailChange = (newEmail: string) => {
        setEditedEmail(newEmail);
        setEditedEmailError(!EMAIL_VALIDATION_REGEX.test(newEmail) ? 'Please enter a valid email.' : '')
    }

	const onClose = () => {
		setIsExitPopupOpen(true)
	}

	const onContinue = () => {
		let canContinue = true;

		if (positionValue === undefined) {
			canContinue = false;
			setPositionValueError('You must select a role to continue')
		}
		if (basisValue === undefined) {
			canContinue = false;
			setBasisValueError('You must select a contract to continue')
		}
		if (locationValue === undefined) {
			canContinue = false;
			setLocationValueError('You must select a location to continue')
		}
		if (requirementValue === undefined) {
			canContinue = false;
			setRequirementValueError('You must select a requirement to continue')
		}
		if (!availabilityArray.length) {
			canContinue = false;
			setAvailabilityArrayError('You must select one or more days to continue')
		}

		if (startDate === null) {
			canContinue = false;
			setStartDateError('You must select a start date to continue')
		}

		if (startDate) {
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const selected = new Date(startDate);
			selected.setHours(0, 0, 0, 0);
			if (selected < today) {
				canContinue = false;
				setStartDateError('Please select today\'s date or a future date for the Start Date');
			}
		}

		if (editedEmail === "") {
			setEditedEmail(notificationEmail)
		}

		if (canContinue) {
			const position = positionValue !== undefined ? findPositionById(positionValue) : undefined;
			const basis = basisValue !== undefined ? findBasisById(basisValue) : undefined;
			const requirement = requirementValue !== undefined ? findRequirementById(requirementValue) : undefined;
			const location = locationValue !== undefined ?
				billingLocations.find(loc => loc.id === locationValue) : undefined;

			if (!position || !basis || !requirement || !location) {
				console.error("Required values not found:", {
					position, basis, requirement, location
				});
				return;
			}

			saveJob({
				...persistedJob,
				basis,
				position,
				requirement,
				location,
				...(startDate && { startDate: startDate as Date }),
				...(approximateStartDate && { approxStart: approximateStartDate }),
				availability: convertAvailabilityForBackend(availabilityArray),
				isOrtho: isOrthodonticFlag,
				isRestorative: isRestorativeFlag,
				notificationEmail: editedEmail !== persistedJob.notificationEmail ? editedEmail : persistedJob.notificationEmail,
				isPublic,
			})

			history.push( { pathname: "editJobSecondStep", state: { mode, originalJob } })
		}
	}

	const onExitPopupClose = () => {
		setIsExitPopupOpen(false);
	}

	const onExitPopupConfirm = () => {
		setIsExitPopupOpen(false);

		if (mode === EditPostMode.edit) {
			history.push({ pathname: "jobBoardDetails", state: { job: originalJob } });
		} else {
			history.replace("jobBoard")
		}
	}

	return (
		<>
			<FirstStep
				availabilityArray={availabilityArray}
				availabilityArrayError={availabilityArrayError}
				setAvailabilityArray={onSetAvailabilityOption}
				basisValue={basisValue}
				basisValueError={basisValueError}
				onBasisValueChange={onSetBasisValue}
				requirementValue={requirementValue}
				requirementValueError={requirementValueError}
				onRequirementValueChange={onSetRequirementValue}
				locationValue={locationValue}
				locationValueError={locationValueError}
				onLocationValueChange={onSetLocationValue}
				positionValue={positionValue}
				positionValueError={positionValueError}
				onPositionValueChange={onSetPositionValue}
				locations={billingLocations}
				startDate={startDate}
				startDateError={startDateError}
				onStartDateChange={onSetStartDate}
				approximateStartDate={approximateStartDate}
				onSetApproximateStartDate={setApproximateStartDate}
				isRestorativeFlag={isRestorativeFlag}
				onSetIsRestorativeFlag={setIsRestorativeFlag}
				isOrthodonticFlag={isOrthodonticFlag}
				onSetIsOrthodonticFlag={setIsOrthodonticFlag}
				editedEmail={editedEmail}
				onEditedEmailChange={(newEmail) => onEditedEmailChange(newEmail)}
				editedEmailError={editedEmailError}
				notificationEmail={notificationEmail}
				onClose={onClose}
				onContinue={onContinue}
				isAllowSearchEngines={isPublic}
				onAllowSearchEnginesChange={setIsPublic}
				featureOnPost={!!persistedJob.featureOnPost}
				onFeatureOnPostChange={(checked) => saveJob({ ...persistedJob, featureOnPost: checked })}
			/>
			<ExitPopup opened={isExitPopupOpen} onConfirm={onExitPopupConfirm} onClose={onExitPopupClose} />
		</>
	)
}
